import { startTransition } from "react";
import type {
  AuthTokenFetcher,
  ConvexReactClient,
  ConvexReactClientOptions,
} from "convex/react";
import { convexRequestIdOf } from "@shared/telemetry/convex-trace";
import { TracedConvexReactClient } from "@shared/telemetry/traced-convex-client";
import { currentPosthogSessionId } from "@/lib/analytics/posthog-session";
import { recordConvexRequestId } from "@/lib/telemetry/convex-request-ids";

function asTransition<Value>(
  listener: ((value: Value) => void) | undefined,
): ((value: Value) => void) | undefined {
  if (listener === undefined) return undefined;
  return (value) => startTransition(() => listener(value));
}

class BrowserConvexReactClient extends TracedConvexReactClient {
  private backendAnsweredAuth = false;
  private isClosed = false;

  get hasBackendAnsweredAuth(): boolean {
    return this.backendAnsweredAuth;
  }

  override close(): Promise<void> {
    this.isClosed = true;
    return super.close();
  }

  override setAuth(
    fetchToken: AuthTokenFetcher,
    onChange?: (isAuthenticated: boolean) => void,
    onRefreshChange?: (isRefreshing: boolean) => void,
  ) {
    if (this.isClosed) return;
    this.backendAnsweredAuth = false;
    const notifySignedIn = asTransition(onChange);
    super.setAuth(
      fetchToken,
      (isAuthenticated) => {
        this.backendAnsweredAuth = true;
        if (isAuthenticated) notifySignedIn?.(isAuthenticated);
        else onChange?.(isAuthenticated);
      },
      asTransition(onRefreshChange),
    );
  }

  override clearAuth() {
    if (this.isClosed) return;
    this.backendAnsweredAuth = false;
    super.clearAuth();
  }
}

export function hasBackendAnsweredAuth(client: ConvexReactClient): boolean {
  return (
    client instanceof BrowserConvexReactClient && client.hasBackendAnsweredAuth
  );
}

export function createBrowserConvexClient(
  address: string,
  options?: ConvexReactClientOptions,
): TracedConvexReactClient {
  return new BrowserConvexReactClient(address, options, {
    traceContext: () => {
      const sessionId = currentPosthogSessionId();
      return sessionId === undefined ? undefined : { sessionId };
    },
    onFailure: (functionName, error) =>
      recordConvexRequestId(convexRequestIdOf(error), functionName),
  });
}
